/**
 * Level Color Utility
 * Returns colors based on user's current level
 */

export const LEVEL_COLORS = {
    // Basic Levels
    'agency': {
        primary: '#3B82F6',      // Blue
        light: '#DBEAFE',
        dark: '#1E40AF',
        gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
    },
    'dealer': {
        primary: '#047857',      // Dark Green
        light: '#A7F3D0',
        dark: '#065F46',
        gradient: 'linear-gradient(135deg, #047857 0%, #065F46 100%)'
    },
    'distributor': {
        primary: '#D97706',      // Dark Amber
        light: '#FDE68A',
        dark: '#B45309',
        gradient: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)'
    },
    'wholesaler': {
        primary: '#7C3AED',      // Dark Purple
        light: '#DDD6FE',
        dark: '#6D28D9',
        gradient: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)'
    },

    // Mega Levels
    'mega agency': {
        primary: '#06B6D4',      // Cyan
        light: '#CFFAFE',
        dark: '#0891B2',
        gradient: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)'
    },
    'mega dealer': {
        primary: '#14B8A6',      // Teal
        light: '#CCFBF1',
        dark: '#0F766E',
        gradient: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)'
    },
    'mega distributor': {
        primary: '#EA580C',      // Dark Orange
        light: '#FED7AA',
        dark: '#C2410C',
        gradient: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)'
    },
    'mega wholesaler': {
        primary: '#EA580C',      // Dark Orange
        light: '#FED7AA',
        dark: '#C2410C',
        gradient: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)'
    },

    // Diamond Levels
    'diamond agency': {
        primary: '#EC4899',      // Pink
        light: '#FCE7F3',
        dark: '#BE185D',
        gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)'
    },
    'diamond distributor': {
        primary: '#A855F7',      // Purple
        light: '#F3E8FF',
        dark: '#7E22CE',
        gradient: 'linear-gradient(135deg, #A855F7 0%, #9333EA 100%)'
    },
    'diamond wholesaler': {
        primary: '#EF4444',      // Red
        light: '#FEE2E2',
        dark: '#B91C1C',
        gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
    },

    // Admin Levels
    'admin': {
        primary: '#1F2937',      // Dark Gray
        light: '#F3F4F6',
        dark: '#111827',
        gradient: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)'
    },
    'company': {
        primary: '#6366F1',      // Indigo
        light: '#E0E7FF',
        dark: '#4338CA',
        gradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)'
    },
    'subadmin': {
        primary: '#64748B',      // Slate
        light: '#F1F5F9',
        dark: '#475569',
        gradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)'
    },

    // Default
    'default': {
        primary: '#6B7280',      // Gray
        light: '#F9FAFB',
        dark: '#374151',
        gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)'
    }
};

/**
 * Get color scheme for a specific level
 * @param {string} level - User's current level
 * @returns {object} Color scheme object
 */
export const getLevelColors = (level) => {
    if (!level) return LEVEL_COLORS.default;

    const normalizedLevel = level.toLowerCase().trim();
    return LEVEL_COLORS[normalizedLevel] || LEVEL_COLORS.default;
};

/**
 * Get primary color for a level
 * @param {string} level - User's current level
 * @returns {string} Hex color code
 */
export const getLevelColor = (level) => {
    return getLevelColors(level).primary;
};

/**
 * Get gradient for a level
 * @param {string} level - User's current level
 * @returns {string} CSS gradient string
 */
export const getLevelGradient = (level) => {
    return getLevelColors(level).gradient;
};

/**
 * Get current user's level from localStorage
 * @returns {string} Current user's level
 */
export const getCurrentUserLevel = () => {
    try {
        const user = JSON.parse(localStorage.getItem('htamsUser') || '{}');
        return user.currentLevel || user.role || 'default';
    } catch (error) {
        console.error('Error getting user level:', error);
        return 'default';
    }
};

/**
 * Get current user's color scheme
 * @returns {object} Color scheme for current user
 */
export const getCurrentUserColors = () => {
    const level = getCurrentUserLevel();
    return getLevelColors(level);
};
